import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';

const TYPE_LABELS = {
  portrait:      'Portrait',
  wedding:       'Wedding',
  commercial:    'Commercial',
  documentary:   'Documentary',
  'wedding-offer': 'Wedding Offer (£999)',
};

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/booking`);
      if (res.ok) {
        const data = await res.json();
        setBookings(data);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/booking/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setBookings(prev => prev.filter(b => b._id !== id));
        if (selected?._id === id) setSelected(null);
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const filtered = filter
    ? bookings.filter(b => b.portfolioType === filter)
    : bookings;

  const types = [...new Set(bookings.map(b => b.portfolioType))];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1">
        <div className="px-5 py-4 bg-black text-white">
          <p className="capitalize text-lg" style={{ fontFamily: 'Muli' }}>Booking Enquiries</p>
        </div>

        <div className="p-8">
          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setFilter('')}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition ${filter === '' ? 'bg-black text-white border-black' : 'border-gray-300 text-gray-600 hover:border-black'}`}
              style={{ fontFamily: 'Muli' }}
            >
              All ({bookings.length})
            </button>
            {types.map(t => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`px-4 py-2 text-xs uppercase tracking-widest border transition ${filter === t ? 'bg-black text-white border-black' : 'border-gray-300 text-gray-600 hover:border-black'}`}
                style={{ fontFamily: 'Muli' }}
              >
                {TYPE_LABELS[t] || t} ({bookings.filter(b => b.portfolioType === t).length})
              </button>
            ))}
          </div>

          <div className="grid xl:grid-cols-3 gap-6">
            {/* Table */}
            <div className="xl:col-span-2 overflow-x-auto">
              <table className="min-w-full border border-gray-200 bg-white">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'Muli' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm" style={{ fontFamily: 'Muli' }}>No bookings found.</td>
                    </tr>
                  )}
                  {filtered.map(b => (
                    <tr
                      key={b._id}
                      className={`hover:bg-gray-50 cursor-pointer ${selected?._id === b._id ? 'bg-gray-50' : ''}`}
                      onClick={() => setSelected(b)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap" style={{ fontFamily: 'Muli' }}>
                        <span className="px-2 py-1 text-[10px] uppercase tracking-wider border border-gray-200 text-gray-600">
                          {TYPE_LABELS[b.portfolioType] || b.portfolioType}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm" style={{ fontFamily: 'Muli' }}>
                        {b.fullName || b.names || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600" style={{ fontFamily: 'Muli' }}>{b.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-400" style={{ fontFamily: 'Muli' }}>
                        {b.submittedAt ? new Date(b.submittedAt).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(b._id); }}
                          className="text-xs text-red-500 hover:text-red-700 uppercase tracking-wider"
                          style={{ fontFamily: 'Muli' }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Detail panel */}
            <div className="bg-white border border-gray-200 p-6">
              {!selected ? (
                <p className="text-gray-400 text-sm uppercase tracking-widest" style={{ fontFamily: 'Muli' }}>
                  Select a row to view details
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <span className="px-2 py-1 text-[10px] uppercase tracking-wider border border-gray-200 text-gray-600" style={{ fontFamily: 'Muli' }}>
                      {TYPE_LABELS[selected.portfolioType] || selected.portfolioType}
                    </span>
                    <button onClick={() => setSelected(null)} className="text-gray-300 hover:text-gray-600 text-xl">×</button>
                  </div>

                  {[
                    { label: 'Full Name',    value: selected.fullName },
                    { label: 'Names',        value: selected.names },
                    { label: 'Email',        value: selected.email },
                    { label: 'Phone',        value: selected.phone },
                    { label: 'Company',      value: selected.company },
                    { label: 'Organisation', value: selected.org },
                    { label: 'Wedding Date', value: selected.weddingDate },
                    { label: 'Venue',        value: selected.venue },
                    { label: 'Location',     value: selected.location },
                    { label: 'Portrait Type',value: selected.portraitType },
                    { label: 'Goal',         value: selected.goal },
                    { label: 'Vision',       value: selected.vision },
                    { label: 'Project Scope',value: selected.scope },
                    { label: 'Timeline',     value: selected.timeline },
                    { label: 'Subject',      value: selected.subject },
                    { label: 'Location & Duration', value: selected.locationDuration },
                    { label: 'Submitted',    value: selected.submittedAt ? new Date(selected.submittedAt).toLocaleString('en-GB') : null },
                  ].filter(f => f.value).map(f => (
                    <div key={f.label}>
                      <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1" style={{ fontFamily: 'Muli' }}>{f.label}</p>
                      <p className="text-sm text-gray-800 leading-relaxed" style={{ fontFamily: 'Muli' }}>{f.value}</p>
                    </div>
                  ))}

                  <button
                    onClick={() => handleDelete(selected._id)}
                    className="mt-4 w-full border border-red-300 text-red-500 py-2 text-xs uppercase tracking-widest hover:bg-red-50 transition"
                    style={{ fontFamily: 'Muli' }}
                  >
                    Delete Booking
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bookings;
